import { useMemo } from 'react'
import { DateTime } from 'luxon'
import orderBy from 'lodash/orderBy'
import { UserSearchInput } from '@/components/UserSearchInput'
import {
  AdminUserTaxCalculation,
  SortPreset,
  UserTaxCalculationStatuses,
  UserTaxCalculationTabNames,
} from '../userTaxCalculation.slice'
import CalculationTableRow from './CalculationTableRow'
import TaxCalculationStatistics from './TaxCalculationStatistics'
import { selectQuarterlyTaxEstimateDetailsByYearAndQuarter } from '../../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { Checkbox, Table } from '@/componentsV1/BaseComponents'
import { Colors } from '../../../../styles/theme'
import { selectIsTaxCoordinator } from '@/selectors/user.selectors'
import { quarterUsesAbound } from '../service'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

interface Props {
  calculations: AdminUserTaxCalculation[]
  calendarYear: string
  calendarQuarter: string
  activeTab: UserTaxCalculationTabNames
  userSearch: string
  onUserSearchChange: (value: string) => void
  readyToCalculateFilter: boolean
  onReadyToCalculateChange: (value: boolean) => void
  sortBy: SortPreset
  onSortChange: (value: SortPreset) => void
  filteredResultCount: number
}

const ItalicAuxiliaryText = ({ content }: { content: string }) => (
  <span
    style={{
      fontStyle: 'italic',
      fontWeight: 'normal',
      float: 'right',
      overflowWrap: 'break-word',
    }}
  >
    {content}
  </span>
)

const whiteBg = { backgroundColor: Colors.white }

enum SORT_BY {
  userName = 'userName',
  taxProfile = 'taxProfile',
}

const CalculationTable = ({
  calculations = [],
  calendarQuarter,
  calendarYear,
  activeTab,
  userSearch,
  onUserSearchChange,
  readyToCalculateFilter,
  onReadyToCalculateChange,
  sortBy,
  onSortChange,
  filteredResultCount,
}: Props) => {
  const isTaxCoordinator = useReselector(selectIsTaxCoordinator)

  const usesAbound = quarterUsesAbound({ calendarYear, calendarQuarter })

  const quarterDetails = useReselector(
    selectQuarterlyTaxEstimateDetailsByYearAndQuarter,
    calendarYear,
    calendarQuarter
  )

  const bookkeepingPeriod = `${
    quarterDetails?.bookkeepingPeriodStartsAt
      ? isoToUTCDateTime(quarterDetails.bookkeepingPeriodStartsAt).toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        )
      : ''
  } - ${
    quarterDetails?.bookkeepingPeriodEndsAt
      ? isoToUTCDateTime(quarterDetails.bookkeepingPeriodEndsAt).toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        )
      : ''
  }`

  const firstCalculatedAt = useMemo(
    () => orderBy(calculations, ['calculatedAt'], ['asc'])[0],
    [calculations]
  )

  const calculationToOccurOn = useMemo(
    () =>
      activeTab === UserTaxCalculationTabNames.singleState ||
      activeTab === UserTaxCalculationTabNames.all
        ? firstCalculatedAt
          ? firstCalculatedAt.calculatedAt
            ? `performed on ${DateTime.fromISO(firstCalculatedAt.calculatedAt).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}`
            : `occurs on ${
                quarterDetails?.calculationStartsAt
                  ? isoToUTCDateTime(
                      quarterDetails.calculationStartsAt
                    ).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
                  : ''
              }`
          : 'no calculations exist'
        : activeTab === UserTaxCalculationTabNames.edgeCases ||
            activeTab === UserTaxCalculationTabNames.multiState
          ? 'to be manually calculated'
          : '',
    [firstCalculatedAt, activeTab, quarterDetails?.calculationStartsAt]
  )

  const checklistLaunchDate = quarterDetails?.checklistLaunchDate
    ? isoToUTCDateTime(quarterDetails.checklistLaunchDate)
    : null
  const paymentDueDate = quarterDetails?.irsPaymentDueAt
    ? isoToUTCDateTime(quarterDetails.irsPaymentDueAt)
    : null

  const isInEditableTimeFrame = useMemo(() => {
    const now = DateTime.now()
    return (
      quarterDetails?.status === 'active' &&
      ((checklistLaunchDate !== null &&
        paymentDueDate !== null &&
        now > checklistLaunchDate &&
        now < paymentDueDate) ||
        (isTaxCoordinator &&
          (activeTab === UserTaxCalculationTabNames.multiState ||
            activeTab === UserTaxCalculationTabNames.edgeCases) &&
          checklistLaunchDate !== null &&
          now > checklistLaunchDate))
    )
  }, [
    quarterDetails?.status,
    checklistLaunchDate,
    paymentDueDate,
    isTaxCoordinator,
    activeTab,
  ])

  const shouldShowCalculations =
    activeTab !== UserTaxCalculationTabNames.edgeCases &&
    activeTab !== UserTaxCalculationTabNames.multiState

  const allowEditReleasedUserTaxEstimates = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.allowEditReleasedUserTaxEstimates,
    false
  )

  const getSortDirection = (
    column: SORT_BY
  ): 'ascending' | 'descending' | undefined => {
    if (column === SORT_BY.userName) {
      if (sortBy === 'userNameAsc') {
        return 'ascending'
      }
      if (sortBy === 'userNameDesc') {
        return 'descending'
      }
      return undefined
    }

    if (sortBy === 'taxProfileAsc') {
      return 'ascending'
    }
    if (sortBy === 'taxProfileDesc') {
      return 'descending'
    }
    return undefined
  }

  const getNextUserNameSort = (): SortPreset =>
    sortBy === 'userNameAsc'
      ? 'userNameDesc'
      : sortBy === 'userNameDesc'
        ? 'default'
        : 'userNameAsc'

  const getNextTaxProfileSort = (): SortPreset =>
    sortBy === 'taxProfileAsc'
      ? 'taxProfileDesc'
      : sortBy === 'taxProfileDesc'
        ? 'default'
        : 'taxProfileAsc'

  return (
    <>
      <TaxCalculationStatistics
        quarterNotificationDate={quarterDetails?.clientNotifiedAt}
        calculations={calculations}
        checklistLaunchDate={
          quarterDetails?.checklistLaunchDate ?? quarterDetails?.newUserCutOffAt
        }
      />
      <div style={{ padding: '0 1em 1em' }}>
        <UserSearchInput value={userSearch} onChange={onUserSearchChange} />
        <div style={{ marginTop: '1em' }}>
          <Checkbox
            label="Use 'Ready to Calculate' Filter"
            checked={readyToCalculateFilter}
            value={readyToCalculateFilter}
            onChange={onReadyToCalculateChange}
          />
        </div>
        <div style={{ marginTop: '1em' }}>
          <p style={{ margin: 0 }}>
            Tab counts show all calculations for this quarter. Showing{' '}
            {filteredResultCount} filtered result
            {filteredResultCount === 1 ? '' : 's'}.
          </p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table
          style={{
            ...whiteBg,
            margin: '0 auto',
            marginBottom: '1em',
            marginTop: '1em',
            minWidth: '1200px',
            width: '98%',
          }}
          basic
          celled
          striped
          sortable
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                width={3}
                style={whiteBg}
                sorted={getSortDirection(SORT_BY.userName)}
                onClick={() => onSortChange(getNextUserNameSort())}
              >
                Customer (sortable)
                <ItalicAuxiliaryText content="(by first name)" />
              </Table.HeaderCell>
              {shouldShowCalculations && (
                <Table.HeaderCell style={whiteBg}>
                  Calculation Inputs
                  <ItalicAuxiliaryText
                    content={'High level details for single state'}
                  />
                </Table.HeaderCell>
              )}
              {shouldShowCalculations && usesAbound && (
                <Table.HeaderCell style={whiteBg}>
                  Calculation Outputs
                  <ItalicAuxiliaryText content={calculationToOccurOn} />
                </Table.HeaderCell>
              )}
              <Table.HeaderCell style={whiteBg}>
                Final Estimates
                <ItalicAuxiliaryText content={'To be shared with customer'} />
              </Table.HeaderCell>
              <Table.HeaderCell
                style={whiteBg}
                sorted={getSortDirection(SORT_BY.taxProfile)}
                onClick={() => onSortChange(getNextTaxProfileSort())}
              >
                Checklist completion status (sortable)
                <ItalicAuxiliaryText content="(by profile last reviewed, nulls always last)" />
              </Table.HeaderCell>
              <Table.HeaderCell width={2} style={whiteBg}>
                Actions
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {calculations.map((c) => (
              <CalculationTableRow
                shouldShowCalculations={shouldShowCalculations}
                key={`calc-${c.id}`}
                id={c.id}
                user={c.user}
                bookkeepingPeriod={bookkeepingPeriod}
                calculationEditHidden={
                  !isInEditableTimeFrame ||
                  (allowEditReleasedUserTaxEstimates
                    ? false
                    : c.status === UserTaxCalculationStatuses.savedAndNotified)
                }
                checklistLaunchDate={
                  quarterDetails?.checklistLaunchDate ??
                  quarterDetails?.newUserCutOffAt
                }
              />
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  )
}

export default CalculationTable
